import React from "react";
import "./middle-truncate.scss";

/**
 * How many trailing characters survive the cut. Tuned for the action picker's bounded columns,
 * where the tail is usually what distinguishes two actions sharing a prefix. Raise it and the
 * head loses room; lower it and the tail stops being informative.
 */
const DEFAULT_TAIL_LENGTH: number = 10;

const WHITESPACE: RegExp = /\s/;

/**
 * Where to cut, given a desired tail length — never on whitespace.
 *
 * A space left at the END of the head is removed by ordinary white-space processing (the head is a
 * block container and trailing spaces do not survive the end of a line), so "Document Management"
 * rendered as "DocumentManagement". Walking the seam back hands that space to the tail instead,
 * where `__tail`'s `white-space: pre` — written for exactly this — keeps it.
 */
const seamIndex = (text: string, tailLength: number): number => {
  let seam: number = text.length - tailLength;
  while (seam > 0 && WHITESPACE.test(text.charAt(seam - 1))) {
    seam -= 1;
  }
  return seam;
};

export interface IMiddleTruncateProps {
  text: string;
  className?: string;
  tailLength?: number;
}

/**
 * Renders `text` so that, when it cannot fit its box, the ellipsis lands in the MIDDLE
 * ("Lorem Ipsum Mole…Cras Eu Q") instead of swallowing the end.
 *
 * There is no measurement anywhere: the head is a shrinkable flex item carrying the ordinary
 * `text-overflow: ellipsis` and the tail is a rigid one, so the browser does the clipping and
 * the tail always survives. No ResizeObserver, no canvas text metrics, no re-render on layout —
 * and it degrades to plain text whenever the label fits. That matters in a cascader that can
 * paint hundreds of rows per open, where a per-row measurement pass would be felt.
 *
 * `title` carries the full string, so hovering a clipped label still reveals all of it.
 */
export const MiddleTruncate = ({
  text,
  className,
  tailLength = DEFAULT_TAIL_LENGTH
}: IMiddleTruncateProps): React.ReactElement => {
  const safeText: string = text ?? "";
  const classes: string = ["kuika-mid-truncate", className].filter(Boolean).join(" ");

  // Too short to split: one plain node, no head/tail pair to reason about.
  if (safeText.length <= tailLength) {
    return (
      <span className={classes} title={safeText}>
        {safeText}
      </span>
    );
  }

  const seam: number = seamIndex(safeText, tailLength);
  // The walk ran off the front — the label is all whitespace up to the tail, so there is no head
  // left to shrink and the pair would only add an empty span.
  if (seam === 0) {
    return (
      <span className={classes} title={safeText}>
        {safeText}
      </span>
    );
  }

  const head: string = safeText.slice(0, seam);
  const tail: string = safeText.slice(seam);

  return (
    <span className={classes} title={safeText}>
      <span className="kuika-mid-truncate__head">{head}</span>
      <span className="kuika-mid-truncate__tail">{tail}</span>
    </span>
  );
};
